import {useCallback, useEffect, useMemo, useRef, useState} from 'react'
import {useSearchParams} from 'react-router'
import {Face3D} from '@holition/face3d'
import {cmsClient} from 'data/cmsClient'
import {NavBackHeader} from 'brand/header/NavBackHeader'
import {expectationsFor, LookExpectations} from 'pages/Looks/LookExpectations'
import {renderTabs, TabsContext} from './Tabs'
import {loadingLook, noLook} from './placeholderLooks'

export const CustomLook = ({selfieData, faceScanResults, recommendedProducts}) => {
  const [params] = useSearchParams()
  const lookId = params.get('id')

  const containerRef = useRef(null)
  const face3dRef = useRef(null)
  const [lookName, setLookName] = useState(lookId ? loadingLook.name : noLook.name)
  const [rawProducts, setRawProducts] = useState(lookId ? loadingLook.products : noLook.products)
  const [lookProducts, setLookProducts] = useState(lookId ? loadingLook.products : noLook.products)
  const [lookExpectations, setLookExpectations] = useState({})

  const [liveVideo, setLiveVideo] = useState(false)
  const toggleLiveVideo = async () => setFace3DInputLive(!liveVideo)

  const setFace3DInputLive = async live => {
    const src = live ? {type: 'webcam'} : {type: 'image', url: selfieData}
    await face3dRef.current.setInput({...src, displayFit: 'cover'})
    setLiveVideo(live)
  }

  const initializeFace3D = async () => {
    try {
      face3dRef.current = await Face3D.create({
        container: containerRef.current,
      })
      await setFace3DInputLive(liveVideo)
    } catch (error) {
      console.error('Error initializing Face3D:', error)
    }
  }

  const sendParamLook = () => {
    if (!lookId) {
      return
    }
    const cmsLook = cmsClient.lookById(lookId)
    if (cmsLook === undefined) {
      console.log('look not returned:', {lookId})
      return
    }

    const {look, renderProducts: products} = cmsLook
    const {name} = look.regions[0]?.meta
    showLookCallback({name, products})()
  }

  const showLookCallback = useCallback(({name, products}) => () => {
    setLookName(name)
    face3dRef.current.sendProducts(products)
    setRawProducts(products)
    setLookProducts(transformLookProducts(products))
    setLookExpectations(expectationsFor({name, productCount: products.length}))
  }, [])

  const init = async () => {
    if (!face3dRef.current) {
      await initializeFace3D()
      sendParamLook()
    }
  }

  useEffect(() => {
    init()
    return () => { face3dRef.current?.destroy() }
  }, []) // Empty dependency array means this runs once on mount

  const [tab, setTab] = useState('About')
  const {Buttons, Content} = useMemo(
    () => renderTabs({
      products: lookProducts,
      rawProducts,
      sendProducts: p => { face3dRef.current?.sendProducts(p) },
      showLookCallback, faceScanResults, recommendedProducts,
    }),
    [lookProducts, rawProducts, showLookCallback, faceScanResults, recommendedProducts]
  )

  return (
    <div className="column">
      <NavBackHeader link="/consult/looks" actionText="Back to my Looks">
        <LiveButton {...{liveVideo, toggleLiveVideo}} />
      </NavBackHeader>
      <div className="heading size-xxxl">
        {lookName}
        {
          lookProducts.length === 0 ? null :
            <div className="subheading size-m">
              <LookExpectations {...lookExpectations} />
            </div>
        }
      </div>
      <TabsContext.Provider value={{tab, setTab}}>
        <div className="top-sticky">
          <div
            ref={containerRef}
            className="face-sdk-container"
            style={liveVideo ? {} : {transform: 'rotateY(180deg)'}}
          />
          <Buttons />
        </div>
        <Content />
      </TabsContext.Provider>
    </div>
  )
}

const LiveButton = ({liveVideo, toggleLiveVideo}) => (
  <div className="live-button" onClick={toggleLiveVideo}>
    <div class="toggle-text">LIVE VIEW</div> <div className={liveVideo ? 'toggle on' : 'toggle off'}>
      <div className="dot"/>
    </div>
  </div>
)

export const transformLookProducts = cmsProducts => {
  // console.log({ untransformed: cmsProducts });
  const transformed = cmsProducts
    .map(({ id, regions }) => 
      regions.map(({ 
        data: { type, colours: dataColours, assets }, 
        meta: { name, description }, 
        rendering: { type: renderType, color: renderColour, material } 
      }) => {
        const primaryAsset = assets?.find(asset => asset.type === "Primary")?.url || null;

        return {
          type: type ?? renderType,
          name,
          description,
          productId: id,
          colours: {
            data: dataColours,
            render: { colour: renderColour, material }
          },
          assetUrl: primaryAsset
        };
      })
    ).flat();
  // console.log({transformed})
  return transformed;
};

